import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { ConfirmModal, Search, TotalResults, type ConfirmModalRef } from '@shared/components/ui';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { usePackaging } from '../../hooks/usePackaging';
import { PackagingFilters } from '../../models/Packaging';
import PackagingGrid from './PackagingGrid';
import PackagingHeader from './PackagingHeader';
import PackagingModals, { PackagingModalsRef } from './PackagingModals';
import PackagingStates from './PackagingStates';
import { useDeletePackaging } from '../../hooks/useDeletePackaging';
import { useResponsiveMaterialPageSize } from '../../hooks/useResponsiveMaterialPageSize';
import { PackagingItem } from '../../models/Packaging';
import { LocationsStockModal, LocationsStockModalRef } from '../modals/LocationsStockModal';

/** Valor en URL para filtrar materiales sin grupo de cajas */
const BOX_GROUP_FILTER_NONE = 'none';

const Main: React.FC = () => {
  const { t } = useTranslation('materialAlmacenModule');
  const localViewPageSize = useResponsiveMaterialPageSize();
  const modalsRef = useRef<PackagingModalsRef>(null);
  const adjustStockModalRef = useRef<LocationsStockModalRef>(null);
  const confirmDeleteRef = useRef<ConfirmModalRef>(null);
  const { deletePackaging } = useDeletePackaging();

  const { params, updateParam } = useQueryParams({
    search: { type: 'string', default: '' },
    box_group_id: { type: 'string', default: '' },
  });

  const searchTerm = params.search;
  const boxGroupId = String(params.box_group_id ?? '').trim();

  // Texto del buscador: estado local + URL al pulsar Enter (mismo patrón que Products/FiltersSection)
  const [searchInputValue, setSearchInputValue] = useState(searchTerm);
  useEffect(() => {
    setSearchInputValue((prev) => (prev === searchTerm ? prev : searchTerm));
  }, [searchTerm]);

  const listFilters = useMemo<PackagingFilters>(() => {
    return {
      limit: 100,
      categories: ['own_box'],
      active: true,
    };
  }, []);

  const listQuery = usePackaging(listFilters);

  const apiItems = listQuery.items;

  // Opciones de filtros únicamente a partir de ítems ya cargados de /packaging (sin llamadas extra)
  const boxGroupOptions = useMemo(() => {
    const groupMap = new Map<number, string>();

    for (const item of apiItems) {
      const gid = item.packaging_box_group_id;
      if (gid != null) {
        const label = item.box_group?.name?.trim() || t('card.box_group_fallback', { id: gid });
        if (!groupMap.has(gid)) {
          groupMap.set(gid, label);
        }
      }
    }

    const toSortedOptions = (map: Map<number, string>) =>
      [...map.entries()]
        .map(([id, name]) => ({ label: name, value: String(id) }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

    let boxOpts = toSortedOptions(groupMap);

    const hasUngrouped = apiItems.some((i) => i.packaging_box_group_id == null);
    const needsNoneOption =
      hasUngrouped || (boxGroupId === BOX_GROUP_FILTER_NONE && !boxOpts.some((o) => o.value === BOX_GROUP_FILTER_NONE));
    if (needsNoneOption) {
      boxOpts = [{ label: t('filter.box_group_none'), value: BOX_GROUP_FILTER_NONE }, ...boxOpts];
    }

    const parsedGid = boxGroupId ? parseInt(boxGroupId, 10) : NaN;
    if (boxGroupId && boxGroupId !== BOX_GROUP_FILTER_NONE && !Number.isNaN(parsedGid) && !groupMap.has(parsedGid)) {
      boxOpts = [{ label: `#${boxGroupId}`, value: boxGroupId }, ...boxOpts];
    }

    return boxOpts;
  }, [apiItems, boxGroupId, t]);

  const filteredItems = useMemo(() => {
    const filterNoGroup = boxGroupId === BOX_GROUP_FILTER_NONE || boxGroupId.toLowerCase() === BOX_GROUP_FILTER_NONE;
    const gid = !filterNoGroup && boxGroupId ? parseInt(boxGroupId, 10) : NaN;
    const filterByGroup = !filterNoGroup && !Number.isNaN(gid) && boxGroupId !== '';
    const q = searchTerm.trim().toLowerCase();

    return apiItems.filter((item: PackagingItem) => {
      if (filterNoGroup && item.packaging_box_group_id != null) {
        return false;
      }
      if (filterByGroup && Number(item.packaging_box_group_id) !== gid) {
        return false;
      }
      if (q) {
        const name = (item.name ?? '').toLowerCase();
        const code = (item.code ?? '').toLowerCase();
        if (!name.includes(q) && !code.includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [apiItems, boxGroupId, searchTerm]);

  // Ventana visible sobre `filteredItems`: al cambiar filtros/búsqueda o breakpoint, volver al primer lote
  const localViewResetKey = useMemo(() => [boxGroupId, searchTerm].join('|'), [boxGroupId, searchTerm]);
  const [localVisibleCount, setLocalVisibleCount] = useState(localViewPageSize);
  useEffect(() => {
    setLocalVisibleCount(localViewPageSize);
  }, [localViewResetKey, localViewPageSize]);

  const visibleItems = useMemo(
    () => filteredItems.slice(0, Math.min(localVisibleCount, filteredItems.length)),
    [filteredItems, localVisibleCount],
  );

  const hasMoreInMemory = localVisibleCount < filteredItems.length;

  const revealMoreInMemory = useCallback(() => {
    setLocalVisibleCount((c) => Math.min(c + localViewPageSize, filteredItems.length));
  }, [filteredItems.length, localViewPageSize]);

  const hasLocalFilters = Boolean(boxGroupId) || Boolean(searchTerm.trim());

  const handleSearchSubmit = useCallback(
    (value?: string) => {
      updateParam('search', (value ?? searchInputValue).trim());
    },
    [updateParam, searchInputValue],
  );

  const activeError = listQuery.error;
  const activeIsLoading = listQuery.isLoading;
  const activeHasNextPage = listQuery.hasNextPage;
  const activeIsFetchingNextPage = listQuery.isFetchingNextPage;
  const activeFetchNextPage = listQuery.fetchNextPage;

  const { lastElementRef } = useInfiniteScroll({
    hasNextPage: activeHasNextPage ?? false,
    isFetchingNextPage: activeIsFetchingNextPage,
    fetchNextPage: activeFetchNextPage,
    enabled: !activeIsLoading && !activeError,
    hasMoreInMemory,
    revealMoreInMemory,
  });

  const handleCreatePackaging = useCallback(() => {
    modalsRef.current?.openCreateModal();
  }, []);

  const handleEditPackaging = useCallback((item: PackagingItem) => {
    modalsRef.current?.openEditModal(item);
  }, []);

  const handleDeletePackaging = useCallback(
    (item: PackagingItem) => {
      confirmDeleteRef.current?.open(
        () => {
          deletePackaging(item.id);
        },
        undefined,
        t('modal.delete.confirm_title'),
        t('modal.delete.confirm_message', { name: item.name }),
      );
    },
    [deletePackaging, t],
  );

  const handleAdjustStock = useCallback((item: PackagingItem) => {
    adjustStockModalRef.current?.open(item);
  }, []);

  const breadcrumbs = useMemo(() => [{ label: t('page.breadcrumb'), path: '/packaging/warehouse-material' }], [t]);

  const headerActions = useMemo(() => {
    const actions = [];

    actions.push({
      id: 'create',
      label: t('actions.create_material'),
      onClick: handleCreatePackaging,
      variant: 'primary' as const,
    });

    return actions;
  }, [handleCreatePackaging, t]);

  usePageNavigation({ breadcrumbs, headerActions });

  const handleRefetch = useCallback(() => {
    listQuery.refetch();
  }, [listQuery]);

  const totalResultsCount = hasLocalFilters ? filteredItems.length : listQuery.total;

  return (
    <div className="my-4 min-w-0 px-4 md:px-6">
      {/* Desktop: grid 1fr + auto → el total nunca queda fuera de vista; sin overflow-x. Móvil: columna. */}
      <div className="mb-4 grid w-full min-w-0 grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center md:gap-x-4">
        <div className="flex min-w-0 w-full flex-col gap-3 md:flex-row md:flex-nowrap md:items-center md:gap-x-2 lg:gap-x-3">
          {/* Búsqueda */}
          <div className="min-w-0 w-full shrink-0 md:w-auto md:max-w-[min(100%,18rem)]">
            <Search
              placeholder={t('filter.search_placeholder')}
              value={searchInputValue}
              onChange={(e) => setSearchInputValue(e.target.value)}
              onEnter={handleSearchSubmit}
              dataTestId="warehouse-material-search"
              inputDataTestId="warehouse-material-search-input"
              wrapperClassName="w-full min-w-0 md:max-w-[18rem]"
              className="min-w-0 max-w-full"
            />
          </div>
          <PackagingHeader
            boxGroupOptions={boxGroupOptions}
            boxGroupId={boxGroupId}
            onBoxGroupIdChange={(v) => updateParam('box_group_id', v)}
          />
        </div>
        <div
          className="flex min-w-0 w-full items-center justify-end md:w-auto md:shrink-0 md:self-center"
          data-testid="warehouse-material-total-results"
        >
          <TotalResults total={totalResultsCount} className="whitespace-nowrap py-0" />
        </div>
      </div>

      <PackagingStates
        isLoading={activeIsLoading}
        error={activeError}
        filteredItemsCount={filteredItems.length}
        apiItemsCount={apiItems.length}
        searchTerm={searchTerm}
        hasLocalFilters={hasLocalFilters}
        hasNextPage={activeHasNextPage ?? false}
        onRefetch={handleRefetch}
        onCreatePackaging={handleCreatePackaging}
      />

      <PackagingGrid
        items={visibleItems}
        error={activeError}
        isFetchingNextPage={activeIsFetchingNextPage}
        lastElementRef={lastElementRef}
        onEdit={handleEditPackaging}
        onDelete={handleDeletePackaging}
        onAdjustStock={handleAdjustStock}
        hasNextPage={(activeHasNextPage ?? false) || hasMoreInMemory}
      />

      <PackagingModals ref={modalsRef} onRefetch={handleRefetch} />
      <LocationsStockModal ref={adjustStockModalRef} onSuccess={handleRefetch} />
      <ConfirmModal ref={confirmDeleteRef} />
    </div>
  );
};

export default Main;
